import { NgIf, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/material/form-field';

@Component({
    selector: 'formly-datepicker',
    template: `
		<mat-form-field [floatLabel]="to.floatLabel" [appearance]="to.appearance" [color]="to.color" [style.width]="'100%'">
			<mat-label *ngIf="to.label && to.hideLabel !== true">
				{{ to.label }}
			</mat-label>

			<input
				matInput
				class="input-control"
				[id]="id"
				[errorStateMatcher]="errorStateMatcher"
				[formControl]="control"
				[matDatepicker]="picker"
				[matDatepickerFilter]="to.datepickerOptions.filter"
				[max]="to.datepickerOptions.max"
				[min]="to.datepickerOptions.min"
				[formlyAttributes]="field"
				[placeholder]="to.placeholder ?? ''"
				[tabindex]="to.tabindex"
				[readonly]="to.readonly"
				[required]="to.required ?? false"
				(dateInput)="to.datepickerOptions.dateInput(field, $event)"
				(dateChange)="to.datepickerOptions.dateChange(field, $event)"
			/>

			<mat-datepicker-toggle matSuffix [for]="picker" [disabled]="to.disabled"></mat-datepicker-toggle>
			<mat-datepicker-toggle *ngIf="!to.disabled" matSuffix (click)="clear()">
				<mat-icon matDatepickerToggleIcon>clear</mat-icon>
			</mat-datepicker-toggle>
			<mat-datepicker-toggle *ngIf="to.disabled" matSuffix [disabled]="to.disabled">
				<mat-icon matDatepickerToggleIcon>clear</mat-icon>
			</mat-datepicker-toggle>

			<mat-datepicker
				#picker
				[color]="to.color"
				[dateClass]="to.datepickerOptions.dateClass"
				[disabled]="to.datepickerOptions.disabled"
				[opened]="to.datepickerOptions.opened"
				[panelClass]="to.datepickerOptions.panelClass"
				[startAt]="to.datepickerOptions.startAt"
				[startView]="to.datepickerOptions.startView"
				[touchUi]="to.datepickerOptions.touchUi"
				[formlyAttributes]="field"
				[calendarHeaderComponent]="to.datepickerOptions.calendarHeaderComponent"
				(monthSelected)="to.datepickerOptions.monthSelected(field, $event, picker)"
				(yearSelected)="to.datepickerOptions.yearSelected(field, $event, picker)"
			>
			</mat-datepicker>

			<mat-error>
				<formly-validation-message [field]="field"></formly-validation-message>
			</mat-error>

			<mat-hint *ngIf="to.description || to.hintStart as hint">
				<ng-container [ngTemplateOutlet]="stringOrTemplate" [ngTemplateOutletContext]="{ content: hint }"> </ng-container>
			</mat-hint>

			<ng-template #stringOrTemplate let-content="content">
				<ng-container *ngIf="!content.createEmbeddedView; else template">{{ content }}</ng-container>
				<ng-template #template>
					<ng-container *ngTemplateOutlet="content"></ng-container>
				</ng-template>
			</ng-template>
		</mat-form-field>
	`,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        MatFormFieldModule,
        NgIf,
        MatInputModule,
        FormsModule,
        MatDatepickerModule,
        ReactiveFormsModule,
        FormlyModule,
        MatIconModule,
        NgTemplateOutlet,
    ],
})
export class FormlyDatepickerTypeComponent extends FieldType<FormlyFieldConfig> {
	get control(): UntypedFormControl {
		return this.formControl as UntypedFormControl;
	}

	defaultOptions = {
		templateOptions: {
			readonly: true,

			datepickerOptions: {
				startView: 'month',
				dateInput: (): void => {
					/**do nothing */
				},
				dateChange: (): void => {
					/**do nothing */
				},
				monthSelected: (): void => {
					/**do nothing */
				},
				yearSelected: (): void => {
					/**do nothing */
				},
			},
		},
	};

	detectChanges(): void {
		(this.options as any)?.detectChanges?.(this.field);
	}

	clear(): void {
		this.formControl.markAsDirty();
		this.formControl.setValue(null);
	}
}
